import React, { useEffect, useState } from "react";
import { useI18n } from "../i18n";
import { enablePush, pushState } from "../utils/push";

/**
 * Offers to buzz this device when a customer reserves something.
 *
 * It lives on the pre-orders screen because that is where the question makes
 * sense. The browser's own prompt is only raised from the button here, after
 * the owner has read what it is for — a cold prompt gets refused, and a refusal
 * is close to permanent.
 *
 * This is for when the dashboard is closed. While it is open, the shop's live
 * connection already announces new orders on screen, with a chime.
 */
const HIDDEN_KEY = "order_alerts_hidden";

const safe = (fn, fallback = null) => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};

export default function OrderAlerts() {
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState(null);
  const [turnedOn, setTurnedOn] = useState(false);
  const [hidden, setHidden] = useState(
    () => safe(() => localStorage.getItem(HIDDEN_KEY)) === "1",
  );

  useEffect(() => {
    let cancelled = false;

    pushState().then((next) => {
      if (!cancelled) setState(next);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const hide = () => {
    safe(() => localStorage.setItem(HIDDEN_KEY, "1"));
    setHidden(true);
  };

  const show = () => {
    safe(() => localStorage.removeItem(HIDDEN_KEY));
    setHidden(false);
  };

  const accept = async () => {
    setBusy(true);
    setFailure(null);

    try {
      const result = await enablePush();
      if (result.ok) setTurnedOn(true);
      else setFailure(result.reason);
    } catch {
      setFailure("failed");
    } finally {
      setBusy(false);
    }
  };

  if (turnedOn) {
    return (
      <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl px-4 py-3">
        {t("preorders.alerts.on")}
      </div>
    );
  }

  // Nothing to offer: still checking, a browser without notifications, a
  // deployment without push keys, or alerts already on for this device
  if (!state || !state.available || state.subscribed) return null;

  if (hidden) {
    return (
      <button
        type="button"
        onClick={show}
        className="text-sm font-medium text-primary-600 hover:underline"
      >
        {t("preorders.alerts.show")}
      </button>
    );
  }

  const shownFailure = failure || (state.reason === "denied" ? "denied" : null);

  return (
    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border-s-4 border-primary-500">
      <div className="flex items-start gap-3">
        <span className="text-2xl" aria-hidden="true">
          🔔
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-bold text-gray-800">
            {t("preorders.alerts.title")}
          </h2>

          {state.reason === "ios-needs-install" ? (
            <>
              <p className="mt-1 text-gray-600">
                {t("preorders.alerts.iosBody")}
              </p>
              <button
                type="button"
                onClick={hide}
                className="mt-4 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium"
              >
                {t("preorders.alerts.gotIt")}
              </button>
            </>
          ) : (
            <>
              <p className="mt-1 text-gray-600">{t("preorders.alerts.body")}</p>

              {shownFailure && (
                <p className="mt-2 text-sm text-red-600">
                  {shownFailure === "denied"
                    ? t("preorders.alerts.denied")
                    : t("preorders.alerts.failed")}
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={accept}
                  disabled={busy}
                  className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-60"
                >
                  {busy ? "…" : t("preorders.alerts.turnOn")}
                </button>
                <button
                  type="button"
                  onClick={hide}
                  className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium"
                >
                  {t("preorders.alerts.notNow")}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                {t("preorders.alerts.confirmHint")}
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
